import Phaser from 'phaser';
import { palette } from '../design/palette';
import type { Ball } from './Ball';
import type { Hole } from './Playfield';

const RING_TEX = 'ball-fallin-ring';

// Split of the total animation duration: 2/7 for slide, 5/7 for drop.
const SLIDE_FRACTION = 2 / 7;

function ensureRingTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(RING_TEX)) return;
  const size = 64;
  const ct = scene.textures.createCanvas(RING_TEX, size, size);
  if (!ct) return;
  const ctx = ct.getContext();
  const cx = size / 2;
  const cy = size / 2;
  const grd = ctx.createRadialGradient(cx, cy, size * 0.30, cx, cy, size * 0.5);
  grd.addColorStop(0.0, 'rgba(255,255,255,0.0)');
  grd.addColorStop(0.5, 'rgba(255,255,255,1.0)');
  grd.addColorStop(1.0, 'rgba(255,255,255,0.0)');
  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, size, size);
  ct.refresh();
}

function fireRingPulse(scene: Phaser.Scene, x: number, y: number, tint: number): void {
  ensureRingTexture(scene);
  const ring = scene.add.image(x, y, RING_TEX)
    .setScale(0.4)
    .setAlpha(1)
    .setDepth(60)
    .setBlendMode(Phaser.BlendModes.ADD)
    .setTint(tint);
  scene.tweens.add({
    targets: ring,
    scale: { from: 0.4, to: 3.0 },
    alpha: { from: 1, to: 0 },
    duration: 260,
    ease: 'Cubic.easeOut',
    onComplete: () => ring.destroy(),
  });
}

/**
 * Play the fall-in animation: slide the ball to the hole's center, then shrink
 * and fade it to zero. Fire a tinted ring pulse on completion. `totalMs` is
 * split 2:5 between the slide and drop stages.
 *
 * Caller MUST gate its own ball-position writes (rolling sim, etc.) while this
 * is in flight — otherwise per-frame setWorldPosition will overwrite the tween.
 */
export function fireBallFallIn(
  scene: Phaser.Scene,
  ball: Ball,
  hole: Hole,
  kind: 'win' | 'fail',
  totalMs: number,
  onComplete: () => void,
): void {
  const slideMs = Math.max(1, Math.round(totalMs * SLIDE_FRACTION));
  const dropMs  = Math.max(1, totalMs - slideMs);
  const ringTint = kind === 'win' ? palette.ballHalo : palette.ballCore;

  const state = { x: ball.x(), y: ball.y(), scale: 1, alpha: 1 };
  const applyToBall = () => ball.setRenderTransform(state.x, state.y, state.scale, state.alpha);

  scene.tweens.add({
    targets: state,
    x: hole.x,
    y: hole.y,
    duration: slideMs,
    ease: 'Cubic.easeOut',
    onUpdate: applyToBall,
    onComplete: () => {
      scene.tweens.add({
        targets: state,
        scale: 0,
        alpha: 0,
        duration: dropMs,
        ease: 'Cubic.easeIn',
        onUpdate: applyToBall,
        onComplete: () => {
          fireRingPulse(scene, hole.x, hole.y, ringTint);
          onComplete();
        },
      });
    },
  });
}
